<template>
  <div class="flex flex-row items-center justify-between">
    <h2 :class="{ 'mb-4': addAgent || booking.agency }">
      {{ t('chooseAgent') }}
    </h2>
    <div v-if="!booking.agency" class="flex flex-col space-y-2">
      <CVButton v-if="!addAgent" @click.prevent="addAgent = true">{{ t('addAgent') }}</CVButton>
    </div>
  </div>
  <div v-if="addAgent" class="relative flex flex-col space-y-2">
    <p class="mb-4">{{ t('addAgentWarning', { days: daysBeforeRentalAgencyInvoiceGetsSent }) }}</p>
    <SearchUserInput
      :allowed-roles="[UserRole.AGENCY, UserRole.COMPANY]"
      :selected-user="booking.agency || undefined"
      class="mb-4 max-w-lg"
      @update-user="handleUpdateAgent"
    />
    <div class="flex space-x-2">
      <CVButton
        variant="default"
        :disabled="!chosenId"
        :is-loading="isSaving"
        @click.prevent="saveAgent"
      >
        {{ t('save') }}
      </CVButton>
      <CVButton
        :is-loading="isSaving"
        @click.prevent="
          chosenId = '';
          addAgent = false;
        "
      >
        {{ t('cancel') }}
      </CVButton>
    </div>
  </div>
  <div v-else-if="booking.agency" class="flex flex-col space-y-2">
    <template v-if="booking.invoicingType === InvoicingType.AGENCY">
      <p>{{ t('agentLinked', { agencyType: t(`agencyTypes.${booking.agency.agencyType}`) }) }}</p>
      <p v-if="booking.hasPeriodicInvoicing">{{ t('agentPeriodicInvoicing') }}</p>
      <template v-else>
        <p v-if="!agencyInvoiceExists">{{ t('invoiceCreatedTime') }} {{ invoiceCreationTime }}</p>
        <p v-else class="text-success">{{ t('agencyInvoiceExists') }}</p>
      </template>
    </template>
    <p v-else-if="booking.agency.role !== UserRole.COMPANY">
      {{ t('commissionAgentInfo', { agencyType: t(`agencyTypes.${booking.agency.agencyType}`) }) }}
    </p>
    <p v-else>
      {{ t('businessCustomerInfo') }}
    </p>
    <div class="justify-between max-sm:space-y-2 md:flex md:items-end">
      <LabeledText
        :label="
          t('linked', {
            agencyType:
              booking.agency.role === UserRole.AGENCY
                ? t(`agencyTypes.${booking.agency.agencyType}`)
                : t('businessCustomer'),
          })
        "
        >{{
          `${booking.agency.firstName} ${booking.agency.lastName}${
            booking.agency.company ? ', ' + booking.agency.company : ''
          }, ${booking.agency.role === UserRole.COMPANY ? t('userRoles.COMPANY') : t(`agencyTypes.${booking.agency.agencyType}`)}`
        }}<br />
        <span>
          {{ t('emailLabel') }}
          {{ booking.agency.agencySettings?.invoiceEmail || booking.agency.email }} </span
        >,
        <span v-if="booking.agency.phone"
          >{{ t('phoneLabel') }} {{ booking.agency.phone }}</span
        ></LabeledText
      >
      <div v-if="!isLoadingInvoices">
        <CVButton
          v-if="!agencyInvoiceExists"
          variant="danger"
          outline
          :is-loading="isSaving"
          @click.prevent="removeAgent"
          >{{ t('removeAgentFromBooking') }}</CVButton
        >
        <p v-else class="italic text-black/20">
          {{ t('agentCannotBeRemoved') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';
import { computed, ref } from 'vue';
import { flattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import type { Booking, UpdateBookingDto } from '@/entities/bookings/booking.entity';
import LabeledText from '@/components/LabeledText.vue';
import { Alert } from '@/services/alert.service';
import { DateTime } from 'luxon';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { getDateFormat } from '@/hooks/use-date-format';
import { useInvoicesWithQuery } from '@/queries/use-invoices';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import { InvoicingType } from '@/entities/bookings/invoicing-type.enum';
import SearchUserInput from '@/components/booking-forms/customer/SearchUserInput.vue';
import { UserRole } from '@/entities/auth/user-role.enum';

const { t } = useI18n();
const props = defineProps<{
  booking: Booking;
}>();

const { isPending: isSaving, mutateAsync: updateBooking } = useUpdateBooking();

const daysBeforeRentalAgencyInvoiceGetsSent = 7;

const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoicesWithQuery({
  filter: [{ bookingIds: [props.booking.id] }],
});

const agencyInvoiceExists = computed(() =>
  flattenPaginatedData(invoicesData).some((invoice) => invoice.customer?.role === UserRole.AGENCY),
);

const addAgent = ref<boolean>(false);
const chosenId = ref('');
const handleUpdateAgent = (user: any | undefined) => {
  chosenId.value = user?.id ?? '';
};
const invoiceCreationTime = useFormattedDate(
  DateTime.fromISO(props.booking.startDate)
    .minus({
      days: daysBeforeRentalAgencyInvoiceGetsSent,
    })
    .toISO(),
  getDateFormat(),
);

const saveAgent = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('finalAddAgentWarning'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('link'),
    cancelButtonText: t('cancel'),
  });
  if (isConfirmed) {
    const updateBookingValues: UpdateBookingDto = {
      agencyId: chosenId.value,
    };
    await updateBooking({
      id: props.booking.id,
      booking: updateBookingValues,
    });
    addAgent.value = false;
  }
};

const removeAgent = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('removeAgentWarning'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('removeAgent'),
    cancelButtonText: t('cancel'),
  });
  if (isConfirmed) {
    const updateBookingValues: UpdateBookingDto = {
      agencyId: null,
    };
    await updateBooking({
      id: props.booking.id,
      booking: updateBookingValues,
    });
    addAgent.value = false;
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "chooseAgent": "Agency / Business Customer",
    "addAgentWarning": "Attention: If an agency is linked, the rental invoice is addressed to this agent. The invoice will be automatically generated and sent {days} days before the start of the rental. The link can be undone only until this happens.",
    "addAgent": "+ Add",
    "linked": "Linked {agencyType}",
    "link": "Link",
    "agentLinked": "This booking is linked to an {agencyType}. The invoice with the rental amount and extras will be sent to this Agency. Only additional costs will be charged to the tenant.",
    "invoiceCreatedTime": "The invoice will be generated automatically on:",
    "agencyInvoiceExists": "The agency invoice has already been created and sent.",
    "agentPeriodicInvoicing": "This booking uses manual invoicing. The agency invoice will not be sent automatically before the rental starts.",
    "finalAddAgentWarning": "Really link booking to agency?",
    "removeAgentFromBooking": "Remove agency from booking",
    "removeAgentWarning": "Do you really want to remove the agency from the booking? The rental invoice will be addressed and sent to the customer instead of the Agency at the end of the rent.",
    "removeAgent": "Remove agency",
    "agentCannotBeRemoved": "Agency cannot be removed",
    "commissionAgentInfo": "This booking was made through an {agencyType}. For questions, you can also contact the agent.",
    "businessCustomerInfo": "This booking was made through a business customer. For questions, you can also contact the business.",
    "agentContact": "Agent Contact",
    "contactPersonLabel": "Contact:",
    "emailLabel": "Email:",
    "phoneLabel": "Phone:",
    "agencyTypes": {
      "AGENCY_ON_COMMISSION_BASIS": "Agent on Commission Basis"
    },
    "businessCustomer": "Business Customer"
  },
  "de": {
    "chooseAgent": "Vermittler / Businesskunde",
    "addAgentWarning": "Achtung: Wenn ein Vermittler verknüpft ist, wird die Mietrechnung an diesen adressiert. Die Rechnung wird {days} Tage vor Mietbeginn automatisch generiert und verschickt. Die Verknüpfung kann nur bis zu diesem Zeitpunkt aufgehoben werden.",
    "addAgent": "+ Hinzufügen",
    "linked": "Verknüpfter {agencyType}",
    "link": "Verknüpfen",
    "agentLinked": "Diese Buchung ist mit einem {agencyType} verknüpft. Die Rechnung mit dem Mietbetrag und den Extras wird an diesen verschickt. Lediglich zusätzlich anfallende Kosten werden dem Mieter in Rechnung gestellt.",
    "invoiceCreatedTime": "Die Rechnung wird voraussichtlich automatisch generiert am:",
    "agencyInvoiceExists": "Die Partner/Vermittlerrechnung wurde bereits erstellt und verschickt.",
    "agentPeriodicInvoicing": "Diese Buchung nutzt manuelle Abrechnung. Die Partner/Vermittlerrechnung wird nicht automatisch vor Mietbeginn verschickt.",
    "finalAddAgentWarning": "Buchung wirklich mit Vermittler verknüpfen?",
    "removeAgentFromBooking": "Vermittler von Buchung entfernen",
    "removeAgentWarning": "Möchtest du die Verlinkung mit dem Vermittler wirklich auflösen? Die Mietrechnung wird dann nicht mehr an den Vermittler, sondern am Ende der Miete an den Kunden adressiert und verschickt werden.",
    "removeAgent": "Vermittler entfernen",
    "agentCannotBeRemoved": "Vermittler kann nicht mehr entfernt werden",
    "commissionAgentInfo": "Diese Buchung wurde durch einen {agencyType} getätigt. Bei Rückfragen kann man sich auch an diesen wenden.",
    "businessCustomerInfo": "Diese Buchung wurde über einen Businesskunden getätigt. Bei Rückfragen kann man sich auch an diesen wenden.",
    "agentContact": "Kontakt Vermittler",
    "contactPersonLabel": "Kontakt:",
    "emailLabel": "E-Mail:",
    "phoneLabel": "Telefon:",
    "agencyTypes": {
      "AGENCY_ON_COMMISSION_BASIS": "Vermittler auf Provisionsbasis"
    },
    "businessCustomer": "Businesskunde"
  },
  "es": {
    "chooseAgent": "Agencia / cliente empresa",
    "addAgentWarning": "Atención: si se vincula una agencia, la factura de alquiler irá dirigida a ese intermediario. La factura se generará y enviará automáticamente {days} días antes del inicio del alquiler. La vinculación solo puede deshacerse hasta entonces.",
    "addAgent": "+ Añadir",
    "linked": "{agencyType} vinculado",
    "link": "Vincular",
    "agentLinked": "Esta reserva está vinculada a un {agencyType}. La factura con el importe del alquiler y los extras se enviará a esta agencia. Solo se cargarán al arrendatario costes adicionales.",
    "invoiceCreatedTime": "La factura se generará automáticamente el:",
    "agencyInvoiceExists": "La factura para la agencia ya se ha creado y enviado.",
    "finalAddAgentWarning": "¿Vincular la reserva a la agencia?",
    "removeAgentFromBooking": "Quitar agencia de la reserva",
    "removeAgentWarning": "¿Seguro que desea quitar la agencia de la reserva? La factura de alquiler se dirigirá y enviará al cliente en lugar de a la agencia al final del alquiler.",
    "removeAgent": "Quitar agencia",
    "agentCannotBeRemoved": "No se puede quitar la agencia",
    "commissionAgentInfo": "Esta reserva se realizó a través de un {agencyType}. Para dudas, también puede contactar con el intermediario.",
    "businessCustomerInfo": "Esta reserva se realizó a través de un cliente empresa. Para dudas, también puede contactar con la empresa.",
    "agentContact": "Contacto del intermediario",
    "contactPersonLabel": "Contacto:",
    "emailLabel": "Correo:",
    "phoneLabel": "Teléfono:",
    "agencyTypes": {
      "AGENCY_ON_COMMISSION_BASIS": "Intermediario por comisión"
    },
    "businessCustomer": "Cliente empresa"
  }
}
</i18n>
